import React from 'react';
import type { WorkflowInterrupt, WorkflowSnapshot } from '../../../types/workflow';
import { formatTimestamp, shortDigest, type EditableWorkflow } from '../../../utils/workflowDsl';
import { Field, Pill, Section, StructuredField } from './WorkflowFormControls';
import { DefValue } from './WorkflowDefinitionView';

export interface WorkflowGraphInspectorProps {
  wf: EditableWorkflow;
  snapshot: WorkflowSnapshot | null;
  onPatchGraph: (patch: Partial<Pick<EditableWorkflow, 'name' | 'entry' | 'metadata'>>) => void;
  onSetInterrupts: (next: WorkflowInterrupt[]) => void;
  onSelectState: (stateId: string) => void;
  onAddState: () => void;
}

const WorkflowGraphInspector: React.FC<WorkflowGraphInspectorProps> = ({ wf, snapshot, onPatchGraph, onSetInterrupts, onSelectState, onAddState }) => {
  const stateIds = Object.keys(wf.states).sort();
  const rev = snapshot?.revision;

  const updateInterrupt = (index: number, patch: Partial<WorkflowInterrupt>) =>
    onSetInterrupts(wf.interrupts.map((it, i) => (i === index ? { ...it, ...patch } : it)));

  return (
    <div className="cwf-inspector">
      <div className="cwf-inspector__head">
        <div className="cwf-inspector__title">
          <i className="fas fa-sitemap" aria-hidden="true" />
          <span>Workflow</span>
        </div>
        <div className="cwf-inspector__actions">
          <button type="button" className="cwf-btn cwf-btn--xs" onClick={onAddState}>
            <i className="fas fa-plus" aria-hidden="true" /> Add state
          </button>
        </div>
      </div>

      <p className="cwf-note">Click a state or a transition on the canvas to edit it. Drag from a node's right or bottom handle to another node to add a transition.</p>

      <Section title="Graph" defaultOpen summary={`${stateIds.length} states · ${wf.edges.length} transitions`}>
        <Field label="name">
          <input className="cwf-input" value={wf.name} onChange={(e) => onPatchGraph({ name: e.target.value })} />
        </Field>
        <Field label="entry" hint="The state a new dialogue starts in.">
          <select className="cwf-input" value={wf.entry} onChange={(e) => onPatchGraph({ entry: e.target.value })}>
            {!wf.entry || !wf.states[wf.entry] ? <option value="">— not set —</option> : null}
            {stateIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </Field>
        <StructuredField label="metadata" value={wf.metadata} onChange={(next) => onPatchGraph({ metadata: (next as Record<string, unknown> | undefined) ?? null })} />
      </Section>

      <Section title="Interrupts" defaultOpen summary={wf.interrupts.length ? `${wf.interrupts.length}` : 'none'}>
        <p className="cwf-note">
          Workflow-level interrupts fire from any state by name and jump to their target; the target is reachable without incoming transitions.
        </p>
        {wf.interrupts.map((it, index) => (
          <div key={index} className="cwf-interrupt">
            <div className="cwf-grid-2">
              <Field label="name">
                <input className="cwf-input cwf-input--mono" value={it.name} onChange={(e) => updateInterrupt(index, { name: e.target.value })} />
              </Field>
              <Field label="target_state_id">
                <select className="cwf-input" value={it.target_state_id} onChange={(e) => updateInterrupt(index, { target_state_id: e.target.value })}>
                  <option value="">— select —</option>
                  {stateIds.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="description">
              <input className="cwf-input" value={it.description || ''} onChange={(e) => updateInterrupt(index, { description: e.target.value || null })} />
            </Field>
            <div className="cwf-inline cwf-inline--end">
              {it.target_state_id && wf.states[it.target_state_id] ? (
                <button type="button" className="cwf-link" onClick={() => onSelectState(it.target_state_id)}>
                  open target
                </button>
              ) : null}
              <button
                type="button"
                className="cwf-btn cwf-btn--xs cwf-btn--danger"
                onClick={() => onSetInterrupts(wf.interrupts.filter((_, i) => i !== index))}
              >
                <i className="fas fa-trash" aria-hidden="true" /> Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="cwf-btn cwf-btn--xs"
          onClick={() => onSetInterrupts([...wf.interrupts, { name: '', target_state_id: stateIds[0] || '', description: null }])}
        >
          <i className="fas fa-plus" aria-hidden="true" /> Add interrupt
        </button>
      </Section>

      {rev ? (
        <Section title="Revision" summary={`v${rev.workflow_version ?? '—'} · ${rev.workflow_status ?? ''}`}>
          <dl className="cwf-dl">
            <dt>workflow_version</dt>
            <dd>
              v{rev.workflow_version ?? '—'} {rev.workflow_status ? <Pill tone={rev.workflow_status === 'published' ? 'ok' : 'neutral'}>{rev.workflow_status}</Pill> : null}
            </dd>
            <dt>workflow_version_id</dt>
            <dd>
              <code>{rev.workflow_version_id || '—'}</code>
            </dd>
            <dt>digest</dt>
            <dd>
              <code title={rev.digest}>{shortDigest(rev.digest, 24)}</code>
            </dd>
            <dt>taken_at</dt>
            <dd>{formatTimestamp(rev.taken_at)}</dd>
            <dt>ontology_revision</dt>
            <dd>{rev.ontology_revision ?? '—'}</dd>
            {snapshot?.source?.workflow_name ? (
              <>
                <dt>source</dt>
                <dd>
                  <code>{snapshot.source.workflow_name}</code>
                  {snapshot.source.stand ? ` (${snapshot.source.stand})` : ''}
                </dd>
              </>
            ) : null}
            {snapshot?.product_config ? (
              <>
                <dt>product_config</dt>
                <dd>
                  #{snapshot.product_config.id ?? '—'} {snapshot.product_config.label ? `· ${snapshot.product_config.label}` : ''}
                </dd>
              </>
            ) : null}
          </dl>
          {rev.component_versions && Object.keys(rev.component_versions).length ? (
            <details className="cwf-details">
              <summary>component_versions pinned by this revision</summary>
              <div className="cwf-details__body">
                <DefValue value={rev.component_versions} />
              </div>
            </details>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
};

export default WorkflowGraphInspector;
